import { useState, useMemo,useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Badge,
  Card,
  CardBody,
  Input,
  Label,
  FormGroup,
  Spinner
} from "reactstrap";
import DataTable from "react-data-table-component";
import Select from "react-select";
import { Btn } from "../../../AbstractElements";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { useAdmin } from "../../../context/AdminContext";
import { UserOrdersModal } from "./UserOrdersModal";
import { formatDate } from "../../utils/formatDate";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

export const InfluencerBonusModal = ({ isOpen, toggle, bonus }) => {
  const { getData, postData } = useDataFetch();
  const { admin } = useAdmin();
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [filters, setFilters] = useState({ code: "", country: "" });
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryingUsers, setRetryingUsers] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(null);
  const validOrderIds = selectedOrderIds || [];

  // Polling automático cada 5 segundos usando React Query
  const { data: settlementData, refetch: refetchSettlement } = getData(
    ["settlement-detail", bonus?._id],
    `/settlements/get/${bonus?._id}`,
    {
      enabled: isOpen && !!bonus?._id,
      refetchInterval: (query) => {
        // Acceder correctamente a paid_status desde el query data
        const paidStatus = query?.state?.data?.data?.paid_status;
        // Solo hacer polling si paid_status es true (procesando)
        if (paidStatus === true) {
          return 5000;
        }
        // Detener polling si paid_status es false o no existe
        return false;
      },
    }
  );

  const {
    data: payoutDetail,
    isLoading: loadingPayoutDetail,
  } = getData(
    ["payout-detail", bonus?._id, selectedUserId],
    "withdrawal/settlement/find-by-order-and-user",
    {
      enabled: !!bonus?._id && !!selectedUserId && isOpen,
    },
    {
      settlementId: bonus?._id,
      userId: selectedUserId,
    }
  );

  const currentSettlement = settlementData?.data || bonus;
  const isPaidStatusActive = currentSettlement?.paid_status === true;
  const isAdmin = admin?.role === "ADMIN";

  const { mutateAsync: triggerPaymentJob } = postData(
    '/withdrawal/settlement/trigger-payment-job',
    {},
    {
      onSuccess: (response) => {
        if (response.status === 200) {
          toast.success('Job de pagos iniciado exitosamente');
        } else {
          toast.error(response.errors || 'Error al iniciar el job de pagos');
        }
        setIsProcessing(false);
      },
      onError: (error) => {
        toast.error('Error al iniciar el job de pagos');
        console.error('Error triggering payment job:', error);
        setIsProcessing(false);
      },
    }
  );

  const { mutateAsync: retryUserPayment } = postData(
    '/withdrawal/settlement/retry-user-payment',
    {},
    {
      onSuccess: (response, variables) => {
        if (response.status === 200) {
          toast.success('Reintento de pago iniciado exitosamente');
        } else {
          toast.error(response.errors || 'Error al reintentar el pago');
        }
        setRetryingUsers(prev => ({ ...prev, [variables.userId]: false }));
      },
      onError: (error, variables) => {
        toast.error('Error al reintentar el pago');
        console.error('Error retrying user payment:', error);
        setRetryingUsers(prev => ({ ...prev, [variables.userId]: false }));
      },
    }
  );

  const handleTriggerPaymentJob = async () => {
    if (!currentSettlement?._id) return;

    // Confirmar antes de iniciar el proceso de pagos
    const result = await Swal.fire({
      title: '¿Iniciar proceso de pagos?',
      html: `
        <p>Estás a punto de iniciar el proceso de pagos para este corte de liquidación.</p>
        <p class="text-warning"><i class="fa fa-exclamation-triangle"></i> Esta acción procesará los pagos usuarios pendientes.</p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, iniciar pagos',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });

    if (!result.isConfirmed) return;

    setIsProcessing(true);
    await triggerPaymentJob({ settlementId: currentSettlement._id });
    // Refetch para obtener el paid_status actualizado y activar el polling
    await refetchSettlement();
  };

  const handleRetryUserPayment = async (userId) => {
    console.log(currentSettlement, userId);
    if (!currentSettlement?._id || !userId) return;
    setRetryingUsers(prev => ({ ...prev, [userId]: true }));
    await retryUserPayment({ settlementId: currentSettlement._id, userId });
    // Refetch para obtener el estado actualizado del pago del usuario
    await refetchSettlement();
  };

  const handleShowPayoutDetail = (userId) => {
    setSelectedUserId(userId);
  };

  const getPaymentStatusBadge = (paidStatus) => {

    let badge = null;

    if (!paidStatus) {
      badge = <Badge color="warning">⏳ Pendiente</Badge>;
    }
    else if (paidStatus === "SUCCESS") {
      badge = <Badge color="success">✅ Pagado</Badge>;
    }
    else if (paidStatus === "PENDING") {
      badge = <Badge color="warning">⏳ Pendiente</Badge>;
    }
    else if (paidStatus === "PROCESSING") {
      badge = <Badge color="info">🔄 Procesando</Badge>;
    }
    else {
      const rejectionReason =
        paidStatus === "REJECTED_BY_MONNET"
          ? "Monnet"
          : paidStatus === "REJECTED_BY_ACCOUNT"
            ? "No tener cuenta"
            : "No cumplir los valores";

      badge = (
        <Badge color="danger">
          ❌ Rechazado ({rejectionReason})
        </Badge>
      );
    }

    return (
      <div className="d-flex align-items-center gap-2">
        {badge}
      </div>
    );
  };

  const { data: ordersResponse, isPending: loadingOrders } = getData(
    ["orders_by_ids", validOrderIds],
    `/order/byOrders/ids?ids=${validOrderIds.join(",")}`,
    { enabled: validOrderIds.length > 0 }
  );

  const { data: countries } = getData(["countries"], "/product/countries-available/all");
  const countryOptions = useMemo(
    () =>
      countries?.data?.countries?.map((c) => ({
        value: c.country_id,
        label: c.name,
      })) || [],
    [countries]
  );

  const filteredData = useMemo(() => {
    if (!currentSettlement?.users) return [];

    return currentSettlement.users
      .filter((u) => {
        if (filters.code && !u.user_id.multyhealth_code.toLowerCase().includes(filters.code.toLowerCase())) {
          return false;
        }
        if (filters.country && u.user_id.country_id !== filters.country) {
          return false;
        }
        return true;
      })
      .map((u) => ({
        user_id: u.user_id._id,
        user: u.user_id.name,
        code: u.user_id.multyhealth_code,
        email: u.user_id.email,
        currencies: u.currencies,
        total_by_currency: u.total_by_currency,
        paid_status: u.paid_status,
      }));
  }, [currentSettlement?.users, filters]);

  const allFilteredUsersPaidSuccess =
    filteredData.length > 0 && filteredData.every((user) => user.paid_status === "SUCCESS");




  const columns = [
    { name: "Código", selector: (row) => row.code, center: true },
    { name: "Usuario", selector: (row) => row.user, sortable: true },
    { name: "Email", selector: (row) => row.email, sortable: true },
    {
      name: "Monedas / Totales",
      cell: (row) => (
        <>
          {Object.entries(row.currencies || {}).map(([currency, details]) => (
            <div key={currency} className="mb-1 d-flex align-items-center">
              <div>
                <strong>{currency}:</strong> {details.value.toLocaleString()} ({details.points} pts)
              </div>
              {details.orders.length > 0 && (
                <Btn
                  attrBtn={{
                    color: "light",
                    className: "shadow-sm border rounded-circle p-2 ms-2",
                    onClick: () => setSelectedOrderIds(details.orders),
                  }}
                >
                  <i className="fa fa-list text-primary"></i>
                </Btn>
              )}
            </div>
          ))}
        </>
      ),
    },
    {
      name: "Estado Pago",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">

          {getPaymentStatusBadge(row.paid_status)}
          {row.paid_status === 'REJECTED_BY_MONNET' && (
            <i
            className="fa fa-eye text-primary"
            style={{ cursor: "pointer" }}
            onClick={() => handleShowPayoutDetail(row.user_id)}
          />)}
        </div>
      ),
      center: true,
      width: "280px"
    },
    {
      name: "Acciones",
      cell: (row) => {
        const canRetry = row.paid_status !== false && row.paid_status !== 'SUCCESS' && row.paid_status !== 'PENDING' && row.paid_status !== 'PROCESSING';
        const isRetrying = retryingUsers[row.user_id];
        console.log("row", row);
        return isAdmin && canRetry ? (
          <Btn
            attrBtn={{
              color: "primary",
              size: "sm",
              onClick: () => handleRetryUserPayment(row.user_id),
              disabled: isRetrying
            }}
          >
            {isRetrying  ? (
              <>
                <Spinner size="sm" className="me-1" />
                Reintentando...
              </>
            ) : (
              <>
                <i className="fa fa-refresh me-1"></i>
                Reintentar
              </>
            )}
          </Btn>
        ) : 'N/A';
      },
      center: true,
      width: "150px",
    },
  ];


  useEffect(() => {

    if (!payoutDetail?.data) return;

    const payout = payoutDetail.data;

    const errors = payout?.notification_payload?.errors || payout?.response_payload?.errors;

    if (errors && errors.length > 0) {

      const errorMessages = errors
        .map(err => `Código: ${err.code}<br/>Mensaje: ${err.message}`)
        .join("<br/><br/>");

      Swal.fire({
        icon: "error",
        title: "Pago rechazado",
        html: errorMessages,
        confirmButtonText: "Cerrar",
        confirmButtonColor: "#d33",
      });

    }

  }, [payoutDetail]);

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="xl">
        <ModalHeader toggle={toggle} className="bg-warning text-dark">
          <i className="fa fa-coins me-2"></i> Detalle de liquidación
        </ModalHeader>
        <ModalBody>
          {currentSettlement ? (
            <>
              {/* 🔹 TOTALES ARRIBA */}
              <Row className="mb-4 g-2">
                <Col md="3" sm="6" xs="12">
                  <Card className="shadow-sm p-2 text-center">
                    <h6 className="mb-1 text-muted">Periodo</h6>
                    <p className="mb-0 fw-bold">
                      {formatDate(currentSettlement.period.start)} -{" "}
                      {formatDate(currentSettlement.period.end)}
                    </p>
                  </Card>
                </Col>
                <Col md="3" sm="6" xs="12">
                  <Card className="shadow-sm p-2 text-center">
                    <h6 className="mb-1 text-muted">Fecha Cálculo</h6>
                    <p className="mb-0 fw-bold">
                      {formatDate(currentSettlement.calculation_date)}
                    </p>
                  </Card>
                </Col>
                {Object.entries(currentSettlement.total_settlement_by_currency || {}).map(
                  ([currency, totals]) => (
                    <Col md="3" sm="6" xs="12" key={currency}>
                      <Card className="shadow-sm p-2 text-center">
                        <h6 className="mb-1">{currency}</h6>
                        <p className="mb-0">
                          <strong>Total:</strong>{" "}
                          {totals.total_value.toLocaleString()}
                        </p>
                        <p className="mb-0">
                          <strong>Puntos:</strong> {totals.total_points}
                        </p>
                      </Card>
                    </Col>
                  )
                )}
              </Row>

              {/* 🔹 FILTROS */}
              <Row className="mb-3 g-2">
                <Col md="3" sm="6" xs="12">
                  <FormGroup>
                    <Label className="form-label mb-1">Código</Label>
                    <Input
                      type="text"
                      placeholder="Filtrar por código"
                      value={filters.code}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, code: e.target.value }))
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md="3" sm="6" xs="12">
                  <FormGroup>
                    <Label className="form-label mb-1">País</Label>
                    <Select
                      classNamePrefix="react-select"
                      isClearable
                      placeholder="Filtrar por país"
                      options={countryOptions}
                      value={
                        filters.country
                          ? {
                            value: filters.country,
                            label: countryOptions.find(
                              (o) => o.value === filters.country
                            )?.label,
                          }
                          : null
                      }
                      onChange={(option) =>
                        setFilters((prev) => ({
                          ...prev,
                          country: option ? option.value : "",
                        }))
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* 🔹 TABLA DE USUARIOS */}
              <h5 className="mb-3">Usuarios</h5>
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                highlightOnHover
                striped
                responsive
                noDataComponent="No hay información"
              />
            </>
          ) : (
            <p className="text-muted">No hay información</p>
          )}
        </ModalBody>

        <ModalFooter>
          {isAdmin && !allFilteredUsersPaidSuccess && (
            <Btn
              attrBtn={{
                color: "success",
                className: "px-4 me-2",
                onClick: handleTriggerPaymentJob,
                disabled: isPaidStatusActive || isProcessing
              }}
            >
              {isProcessing ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Procesando...
                </>
              ) : isPaidStatusActive ? (
                <>
                  <i className="fa fa-clock me-2"></i> Procesando Pagos...
                </>
              ) : (
                <>
                  <i className="fa fa-play me-2"></i> Iniciar Pagos
                </>
              )}
            </Btn>
          )}
          <Btn attrBtn={{ color: "danger", className: "px-4", onClick: toggle }}>
            <i className="fa fa-times me-2"></i> Cerrar
          </Btn>
        </ModalFooter>
      </Modal>

      <UserOrdersModal
        orderIds={validOrderIds}
        toggle={() => setSelectedOrderIds([])}
        ordersResponse={ordersResponse}
        loadingOrders={loadingOrders}
      />
    </>
  );
};
